/**
 * Reset password screen — route: `/reset-password?token=<token-from-email>`.
 * Calls POST `{baseURL}/BranchResetPassword?token=...` with JSON body
 * `{ password, retype_password }` (token only in query; matches backend / routes.js).
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Login.module.css';
import { branchResetPasswordAPI } from '../../context-api/api-services/BranchResetPassword.service';
import { API_NETWORK_ERROR } from '../../utils/constants';

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

function normalizeNetworkMessage(res) {
  const raw =
    (typeof res?.message === 'string' && res.message.trim()) ||
    (typeof res?.error?.message === 'string' && res.error.message.trim()) ||
    '';
  return raw || API_NETWORK_ERROR;
}

/** Left branding column — matches Login layout. */
function ResetVisualPanel() {
  return (
    <div className={styles.visualPanel}>
      <div className={styles.gridOverlay} />
      <div className={`${styles.ring} ${styles.ring1}`} />
      <div className={`${styles.ring} ${styles.ring2}`} />
      <div className={`${styles.ring} ${styles.ring3}`} />
      <div className={`${styles.orb} ${styles.orb1}`} />
      <div className={`${styles.orb} ${styles.orb2}`} />
      <div className={`${styles.orb} ${styles.orb3}`} />
      <div className={`${styles.orb} ${styles.orb4}`} />
      <div className={`${styles.dot} ${styles.dot1}`} />
      <div className={`${styles.dot} ${styles.dot2}`} />
      <div className={`${styles.dot} ${styles.dot3}`} />
      <div className={`${styles.dot} ${styles.dot4}`} />
      <div className={`${styles.dot} ${styles.dot5}`} />
      <div className={styles.visualContent}>
        <div className={styles.brandIcon}>
          <span className={styles.brandIconInner}>
            R<span className={styles.brandIconInnerAccent}>B</span>
          </span>
        </div>
        <p className={styles.brandHeadline}>Account security</p>
        <h2 className={styles.brandTitle}>
          Reset your
          <span className={styles.brandTitleAccent}> access.</span>
        </h2>
        <p className={styles.brandSubtitle}>
          Use the secure link from your email. Your reset token is validated on the server before
          your new password is saved.
        </p>
        <div className={styles.statsRow}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>
              <span className={styles.statAccent}>10</span>m
            </span>
            <span className={styles.statLabel}>Link window</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>Encrypted</span>
            <span className={styles.statLabel}>Token</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>
              <span className={styles.statAccent}>1</span>-click
            </span>
            <span className={styles.statLabel}>Sign in after</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BranchResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';

  const [password, setPassword] = useState('');
  const [retypePassword, setRetypePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRetype, setShowRetype] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ password: false, retype: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validate = () => {
    const passErr = password.length < 1;
    const reErr = retypePassword.length < 1 || retypePassword !== password;
    setFieldErrors({ password: passErr, retype: reErr });
    if (passErr) {
      setError('Password is required.');
      return false;
    }
    if (retypePassword !== password) {
      setError('Password and confirm password must match.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing reset token. Open the link from your email or request a new reset.');
      return;
    }
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await branchResetPasswordAPI(token, {
        password,
        retype_password: retypePassword,
      });

      if (res?.hasError) {
        setError(normalizeNetworkMessage(res));
        return;
      }

      const body = res?.data ?? {};
      const { fetchApiTime: _f, ...serverBody } = body;

      if (serverBody.success === true) {
        const okMsg =
          typeof serverBody.CustomMessage === 'string' && serverBody.CustomMessage.trim()
            ? serverBody.CustomMessage.trim()
            : 'Password update successfully!';
        setSuccessMessage(okMsg);
        setSuccess(true);
        setError('');
        return;
      }

      const msg =
        typeof serverBody.CustomMessage === 'string' && serverBody.CustomMessage.trim()
          ? serverBody.CustomMessage.trim()
          : 'Could not reset password. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <ResetVisualPanel />

      <div className={styles.formPanel}>
        <div className={styles.formInner}>
          <div className={styles.logoArea}>
            <div className={styles.logoBadge}>
              R<span className={styles.logoBadgeAccent}>B</span>
            </div>
            <div className={styles.logoText}>
              <span className={styles.logoName}>Regional Branch</span>
              <span className={styles.logoTagline}>Kitchen Management</span>
            </div>
          </div>

          <div className={styles.headingArea}>
            <h1 className={styles.greeting}>
              New <span className={styles.greetingAccent}>password.</span>
            </h1>
            <p className={styles.subHeading}>
              Enter and confirm your new password. Your email link should open this page with a
              reset token in the URL query string.
            </p>
          </div>

          {!token && (
            <div className={styles.errorAlert} style={{ marginBottom: 16 }}>
              <span className={styles.errorIcon}>&#9888;</span>
              <span className={styles.errorText}>
                No token was found in the address bar. Open the reset link from your email (it
                should look like /reset-password?token=…), or request a new forgot-password email
                from the sign-in page.
              </span>
            </div>
          )}

          {success && (
            <div className={styles.successAlert} role="status" style={{ marginBottom: 16 }}>
              <span className={styles.successText}>{successMessage}</span>
            </div>
          )}

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {error && (
              <div className={styles.errorAlert}>
                <span className={styles.errorIcon}>&#9888;</span>
                <span className={styles.errorText}>{error}</span>
              </div>
            )}

            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel} htmlFor="reset-password">New password</label>
              <div className={styles.inputIconWrapper}>
                <input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFieldErrors((p) => ({ ...p, password: false }));
                    if (error) setError('');
                  }}
                  className={`${styles.inputField} ${styles.inputWithIcon} ${fieldErrors.password ? styles.inputError : ''}`}
                  disabled={loading || success || !token}
                />
                <button
                  type="button"
                  className={styles.inputEndIcon}
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={loading || success || !token}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel} htmlFor="reset-retype">Confirm password</label>
              <div className={styles.inputIconWrapper}>
                <input
                  id="reset-retype"
                  type={showRetype ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  value={retypePassword}
                  onChange={(e) => {
                    setRetypePassword(e.target.value);
                    setFieldErrors((p) => ({ ...p, retype: false }));
                    if (error) setError('');
                  }}
                  className={`${styles.inputField} ${styles.inputWithIcon} ${fieldErrors.retype ? styles.inputError : ''}`}
                  disabled={loading || success || !token}
                />
                <button
                  type="button"
                  className={styles.inputEndIcon}
                  onClick={() => setShowRetype((v) => !v)}
                  tabIndex={-1}
                  aria-label={showRetype ? 'Hide password' : 'Show password'}
                  disabled={loading || success || !token}
                >
                  {showRetype ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {!success ? (
              <button type="submit" className={styles.loginBtn} disabled={loading || !token}>
                {loading ? (
                  <>
                    <span className={styles.spinner} />
                    <span>Updating…</span>
                  </>
                ) : (
                  <>
                    <LockIcon />
                    <span>Update password</span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                className={styles.loginBtn}
                onClick={() => navigate('/login', { replace: true })}
              >
                Go to sign in
              </button>
            )}

            <p className={styles.footerText} style={{ marginTop: 24 }}>
              <Link to="/login" className={styles.footerLink}>
                Back to sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
